import * as React from "react";

import { type VariantProps, cva } from "class-variance-authority";
import { twMerge } from "tailwind-merge";

const calloutVariants = cva("relative flex w-full rounded-lg p-4", {
  variants: {
    color: {
      gray: "bg-gray-100 text-gray-900",
      red: "bg-red-50 text-red-800",
      green: "bg-green-50 text-green-800",
      blue: "bg-blue-50 text-blue-800",
      yellow: "bg-yellow-50 text-yellow-800",
    },
  },
  defaultVariants: {
    color: "gray",
  },
});

const CalloutContext = React.createContext<{
  color: VariantProps<typeof calloutVariants>["color"];
}>({
  color: "gray",
});

const Callout = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & VariantProps<typeof calloutVariants>
>(({ className, color, ...props }, ref) => (
  <CalloutContext.Provider value={{ color }}>
    <div
      ref={ref}
      role="alert"
      className={twMerge(calloutVariants({ color }), className)}
      {...props}
    />
  </CalloutContext.Provider>
));
Callout.displayName = "Callout";

const calloutIconVariants = cva("size-4 shrink-0", {
  variants: {
    color: {
      gray: "text-gray-400",
      red: "text-red-400",
      green: "text-green-400",
      blue: "text-blue-400",
      yellow: "text-yellow-400",
    },
  },
});

const CalloutIcon = ({
  icon,
  className,
}: {
  icon: React.JSX.Element;
  className?: string;
}) => {
  const { color } = React.useContext(CalloutContext);
  return React.cloneElement(icon, {
    className: twMerge(calloutIconVariants({ color }), className),
  });
};
CalloutIcon.displayName = "CalloutIcon";

const CalloutTitle = React.forwardRef<
  HTMLHeadingElement,
  React.HTMLAttributes<HTMLHeadingElement>
>(({ className, ...props }, ref) => (
  <h5
    ref={ref}
    className={twMerge(
      "mb-1 flex items-center pl-2 font-medium leading-none tracking-tight",
      className,
    )}
    {...props}
  />
));
CalloutTitle.displayName = "CalloutTitle";

const CalloutDescription = React.forwardRef<
  HTMLParagraphElement,
  React.HTMLAttributes<HTMLParagraphElement>
>(({ className, ...props }, ref) => (
  <div
    ref={ref}
    className={twMerge("pl-2 text-sm [&_p]:leading-relaxed", className)}
    {...props}
  />
));
CalloutDescription.displayName = "CalloutDescription";

export { Callout, CalloutIcon, CalloutTitle, CalloutDescription };
